<template>
    <div v-if="model">
        <rkm-area
            no-margin-top
            title="Filtros"
            type="fieldset"
        >
            <rkm-form
                ref="Form"
                show-legend
            >
                <rkm-row>
                    <rkm-col cols="6">
                        <rkm-form-date-picker
                            :end-validations="validations.periodo"
                            :range-inline="false"
                            :start-validations="validations.periodo"
                            end-label="Até"
                            range
                            start-label="Período de"
                            v-model="model.periodo"
                        />
                    </rkm-col>

                    <rkm-col cols="6">
                        <rkm-form-select
                            :options="form.unidades"
                            :validations="validations.unidade"
                            @change="handleUnidadeChange"
                            default-option-text="Todos"
                            label="Unidade"
                            show-default-option
                            text-field="nomeFantasia"
                            v-model="model.unidadeId"
                            value-field="id"
                        />
                    </rkm-col>
                </rkm-row>

                <rkm-row>
                    <rkm-col cols="6">
                        <rkm-form-select
                            :options="form.infraestruturas"
                            @change="handleInfraestruturaChange"
                            default-option-text="Todos"
                            label="Local de atendimento"
                            show-default-option
                            text-field="nome"
                            v-model="model.infraestruturaId"
                            value-field="id"
                        />
                    </rkm-col>

                    <rkm-col cols="6">
                        <rkm-form-select
                            :options="form.usuarios"
                            @change="handleUsuarioChange"
                            default-option-text="Todos"
                            label="Aplicador"
                            show-default-option
                            text-field="nome"
                            v-model="model.usuarioId"
                            value-field="id"
                        />
                    </rkm-col>
                </rkm-row>

                <rkm-row>
                    <rkm-col cols="3">
                        <rkm-form-select
                            :options="form.status"
                            default-option-text="Todos"
                            label="Status"
                            show-default-option
                            v-model="model.status"
                        />
                    </rkm-col>

                    <rkm-col cols="3">
                        <rkm-button
                            :label="false"
                            @click="$refs.Form.validate(search)"
                            color="primary"
                        >
                            Buscar
                        </rkm-button>

                        <rkm-button
                            :label="false"
                            @click="reset"
                        >
                            Limpar
                        </rkm-button>
                    </rkm-col>
                </rkm-row>
            </rkm-form>
        </rkm-area>

        <rkm-area
            no-margin-top
            title="Resultado"
        >
            <rkm-table
                :fields="fields.table"
                :items="items"
            >
                <template v-slot:cell(informacoesEnvio)="{index, item}">
                    <rkm-button
                        :id="uniqueId(`envio-info-${index}`)"
                        class="mr-2"
                        small
                    >
                        <rkm-icon icon="info" />
                    </rkm-button>

                    <rkm-popover
                        :target="uniqueId(`envio-info-${index}`)"
                        placement="top"
                        title="Informações de envio"
                        triggers="hover"
                    >
                        <div>
                            <span>
                                <strong>Data/Hora do envio:</strong> {{item.dataHoraEnvio}}
                            </span>
                        </div>

                        <div>
                            <span>
                                <strong>Identificador do solicitante:</strong> {{item.identificadorSolicitante}}
                            </span>
                        </div>

                        <div>
                            <span>
                                <strong>CNS do solicitante:</strong> {{formatCns(item.cnsSolicitante)}}
                            </span>
                        </div>

                        <div>
                            <span>
                                <strong>Identificador do documento:</strong> {{item.identificadorDocumento}}
                            </span>
                        </div>
                    </rkm-popover>
                </template>

                <template v-slot:cell(paciente)="{item}">
                    {{item.pacienteNome}} / {{item.pacienteCns ? formatCns(item.pacienteCns) : null}}
                </template>

                <template v-slot:cell(actions)="{item}">
                    <rkm-button
                        :disabled="item.inconsistencias === 0"
                        @click="send(item)"
                        color="primary"
                        small
                        title="Enviar"
                    >
                        <rkm-icon icon="upload" />
                    </rkm-button>

                    <rkm-button
                        :disabled="item.inconsistencias === 0"
                        @click="$refs.ModalInconsistencia.open(item.documentoImunologicoRotinaId)"
                        color="warning"
                        small
                        title="Inconsistências"
                    >
                        <rkm-icon icon="exclamation" />
                    </rkm-button>
                </template>
            </rkm-table>
        </rkm-area>

        <ModalInconsistencia ref="ModalInconsistencia" />
    </div>
</template>

<script>
// Components.
import ModalInconsistencia from './components/inconsistencia/Modal'

// Mixins.
import {error, file, formats, repository, stringCase} from '@/mixins'

// Others.
import moment from 'moment'

// Vuex.
import {mapActions} from 'vuex'

export default {
    components: {ModalInconsistencia},
    computed: {
        validations() {
            return {
                periodo: {required: true},
            }
        },
    },
    created() {
        this.repositories = {
            gerenciamentoEnvio: this.getConectaSusRepository('integracao.ria.gerenciamentoEnvio'),
        }
    },
    data() {
        return {
            form: null,
            fields: {
                table: [
                    {class: 'col-min text-center', key: 'informacoesEnvio', label: '#'},
                    {key: 'unidadeNomeFantasia', label: 'Unidade', sortable: true},
                    {key: 'infraestruturaNome', label: 'Local de atendimento', sortable: true},
                    {key: 'usuarioNome', label: 'Aplicador', sortable: true},
                    {key: 'pacienteNome', label: 'Paciente', sortable: true},
                    {key: 'dataAplicacao', label: 'Data de aplicação', sortable: true},
                    {key: 'vacinaNome', label: 'Vacina', sortable: true},
                    {key: 'lote', label: 'Lote', sortable: true},
                    {key: 'dataValidadeLote', label: 'Data de validade do lote', sortable: true},
                    {key: 'doseNome', label: 'Dose', sortable: true},
                    {key: 'grupoAtendimentoNome', label: 'Grupo de atendimento', sortable: true},
                    {key: 'estrategiaNome', label: 'Estratégia', sortable: true},
                    {key: 'produtorNome', label: 'Produtor', sortable: true},
                    {class: 'col-min text-center', key: 'actions', label: 'Ações'},
                ],
            },
            items: [],
            model: null,
            others: {moment},
        }
    },
    methods: {
        ...mapActions(['setLoading', 'unsetLoading']),
        load() {
            this.setLoading()

            this.repositories.gerenciamentoEnvio.create()
                .then((response) => {
                    this.form = response.data.form
                    this.model = response.data.model
                })
                .catch(this.handleError)
                .finally(() => this.unsetLoading())
        },
        search() {
            const request = {
                periodo: {
                    start: this.others.moment(this.model.periodo.start).format('DD/MM/YYYY'),
                    end: this.others.moment(this.model.periodo.end).format('DD/MM/YYYY'),
                },
                unidadeId: this.model.unidadeId,
                infraestruturaId: this.model.infraestruturaId,
                usuarioId: this.model.usuarioId,
                status: this.model.status,
            }

            this.setLoading()

            this.repositories.gerenciamentoEnvio.index(this.camelObjectKeysToSnakeRecursive(request))
                .then(response => this.items = Object.assign(response.data))
                .catch(this.handleError)
                .finally(() => this.unsetLoading())
        },
        reset() {
            this.form.unidades = []
            this.form.infraestruturas = []
            this.form.usuarios = []
            this.form.status = []

            this.model.periodo = {
                start: null,
                end: null,
            }

            this.model.unidadeId = null
            this.model.infraestruturaId = null
            this.model.usuarioId = null
            this.model.status = null
            this.items = []

            this.$nextTick().then(() => this.$refs.Form.resetValidator())
        },
        send(item) {
            const request = {documentoImunologicoRotinaId: item.documentoImunologicoRotinaId}

            this.setLoading()

            this.repositories.gerenciamentoEnvio.send(this.camelObjectKeysToSnakeRecursive(request))
                .then(response => {
                    this.showSaveNotification('Registro enviado com sucesso!')

                    if (response.data.issue) {
                        item.inconsistencias = response.data.issue.length
                    } else {
                        this.items.splice(this.items.indexOf(item), 1)
                    }
                })
                .catch(this.handleError)
                .finally(() => this.unsetLoading())
        },
        handleUnidadeChange(value) {
            this.form.infraestruturas = []
            this.form.usuarios = []
            this.form.status = []
            this.model.infraestruturaId = null
            this.model.usuarioId = null
            this.model.status = null
            this.items = []

            const request = {
                listar: 'infraestruturas',
                periodo: {
                    start: this.others.moment(this.model.periodo.start).format('DD/MM/YYYY'),
                    end: this.others.moment(this.model.periodo.end).format('DD/MM/YYYY'),
                },
                unidadeId: value,
            }

            this.setLoading()

            this.repositories.gerenciamentoEnvio.list(this.camelObjectKeysToSnakeRecursive(request))
                .then(response => {
                    this.form.infraestruturas = Object.assign(response.data.infraestruturas)
                    this.form.usuarios = Object.assign(response.data.usuarios)
                    this.form.status = Object.assign(response.data.status)
                })
                .catch(this.handleError)
                .finally(() => this.unsetLoading())
        },
        handleInfraestruturaChange(value) {
            this.form.usuarios = []
            this.form.status = []
            this.model.usuarioId = null
            this.model.status = null
            this.items = []

            const request = {
                listar: 'usuarios',
                periodo: {
                    start: this.others.moment(this.model.periodo.start).format('DD/MM/YYYY'),
                    end: this.others.moment(this.model.periodo.end).format('DD/MM/YYYY'),
                },
                unidadeId: this.model.unidadeId,
                infraestruturaId: value,
            }

            this.setLoading()

            this.repositories.gerenciamentoEnvio.list(this.camelObjectKeysToSnakeRecursive(request))
                .then(response => {
                    this.form.usuarios = Object.assign(response.data.usuarios)
                    this.form.status = Object.assign(response.data.status)
                })
                .catch(this.handleError)
                .finally(() => this.unsetLoading())
        },
        handleUsuarioChange(value) {
            this.form.status = []
            this.model.status = null
            this.items = []

            const request = {
                listar: 'status',
                periodo: {
                    start: this.others.moment(this.model.periodo.start).format('DD/MM/YYYY'),
                    end: this.others.moment(this.model.periodo.end).format('DD/MM/YYYY'),
                },
                unidadeId: this.model.unidadeId,
                infraestruturaId: this.model.infraestruturaId,
                usuarioId: value,
            }

            this.setLoading()

            this.repositories.gerenciamentoEnvio.list(this.camelObjectKeysToSnakeRecursive(request))
                .then(response => {
                    this.form.status = Object.assign(response.data.status)
                })
                .catch(this.handleError)
                .finally(() => this.unsetLoading())
        },
    },
    mixins: [error, file, formats, repository, stringCase],
    mounted() {
        this.load()
    },
    watch: {
        'model.periodo': {
            deep: true,
            handler(value) {
                this.form.unidades = []
                this.form.infraestruturas = []
                this.form.usuarios = []
                this.form.status = []
                this.model.unidadeId = null
                this.model.infraestruturaId = null
                this.model.usuarioId = null
                this.model.status = null
                this.items = []

                if (value.start === null || value.end === null)  {
                    return
                }

                const request = {
                    listar: 'unidades',
                    periodo: {
                        start: this.others.moment(value.start).format('DD/MM/YYYY'),
                        end: this.others.moment(value.end).format('DD/MM/YYYY'),
                    },
                }

                this.setLoading()

                this.repositories.gerenciamentoEnvio.list(this.camelObjectKeysToSnakeRecursive(request))
                    .then(response => {
                        this.form.unidades = Object.assign(response.data.unidades)
                        this.form.infraestruturas = Object.assign(response.data.infraestruturas)
                        this.form.usuarios = Object.assign(response.data.usuarios)
                        this.form.status = Object.assign(response.data.status)
                    })
                    .catch(this.handleError)
                    .finally(() => this.unsetLoading())
            },
        },
    },
}
</script>